import fetchRequest from '@/api/api';
import type { BaseApiResponse, BaseApiResult, ListPageModel } from '@/interfaces/service/api.interface';
import type {
  AppStoreApiResponse,
  AppStoreDto,
  ChannelAccountAuthorizationDto,
  ChannelId,
  CrawlingResultStatusType,
  CrawlingResultType,
  Post,
  PostWordData,
  PostsCountsByDurations,
  PostsCountsByGroup,
  PostsDto,
  ReplySubmitDto,
  ReplySubmitResponse,
  ReviewCountByPeriod
} from '@/interfaces/service/appstore/appstore.interface';
import type { BuisinessNameSearchRes, BuisinessSearchResult } from '@/interfaces/service/appstore/appstore.search.interface';
import { POST_PAGGE_SIZE } from '@/constants/appstore/post.constants';
import { checkHttpRequestResult } from '@/utils/api.util';
import { NO_APP_STORE_ID, isAppStoreId } from '@/utils/appStoreId.util';
import { reportHandledError } from '@/lib/frontend-error-reporter';
import { REVIEWS, STORE } from '@/libs/constants/url';

/**
 * 병원 등록 결과.
 *
 * 구 시그니처는 실패를 전부 `undefined` 하나로 뭉갰다. 그래서 호출부가
 * "왜 실패했는지"는커녕 "실패했다"는 것조차 성공(값 없음)과 구분하지 못했고,
 * 등록 화면은 전체를 덮는 인디케이터를 켠 채 아무 안내 없이 멈춰 있었다.
 * 실패 사유를 남겨서 화면이 분기할 수 있게 한다.
 */
export type CreateAppStoreResult =
  | { isSuccess: true; appStore: AppStoreDto }
  | {
      isSuccess: false;
      /** 응답 본문의 status. 이 프로젝트는 HTTP 상태가 아니라 본문 status 로 판정한다(api.util.ts). */
      status?: string;
      /** 서버가 주는 안정 코드. 문구가 바뀌어도 분기가 깨지지 않게 이 값을 우선 본다. */
      code?: string;
      message?: string;
      /** 이미 등록된 병원일 때 서버가 알려주는 기존 store id (주는 경우에만 존재). */
      duplicatedStoreId?: number;
    };

/** 서버가 실어 보내는 오류 코드를 꺼낸다. 확정 계약은 legacy 오류 봉투의 `errors[0].code` 다. */
const readServerErrorCode = (response: { code?: unknown; data?: unknown; result?: unknown; errors?: unknown }): string | undefined => {
  const payload = (response?.data ?? response?.result) as { code?: unknown } | undefined;
  const firstError = (Array.isArray(response?.errors) ? response.errors[0] : undefined) as { code?: unknown } | undefined;

  // 확정 계약은 errors[0].code 다. 나머지는 서버 구현이 흔들릴 때를 위한 여분이며 없으면 그냥 안 걸린다.
  const candidates = [firstError?.code, payload?.code, response?.code];

  return candidates.find((candidate): candidate is string => typeof candidate === 'string' && candidate.length > 0);
};

/**
 * 중복 응답이 기존 store id 를 실어 보내면 꺼낸다.
 *
 * 항상 오는 값이 아니다 — 서버가 DB 제약 위반으로 잡아낸 경우(동시 요청 경합, 또는 기존 행이
 * 비활성이라 사전 조회에 안 걸린 경우)에는 트랜잭션이 abort 되어 재조회를 못 하므로 null 이다.
 * 그래서 호출부는 이 값이 없는 경우를 반드시 감당해야 한다.
 */
const readDuplicatedStoreId = (response: { data?: unknown; result?: unknown; errorMessage?: unknown }): number | undefined => {
  // legacy 오류 봉투는 부가 정보를 errorMessage 에 싣는다. 성공 봉투(data/result)도 함께 본다.
  const carriers = [response?.errorMessage, response?.data, response?.result];
  const rawStoreId = carriers.map((carrier) => (carrier as { storeId?: unknown } | undefined)?.storeId).find((storeId) => storeId !== undefined && storeId !== null);

  // Number(null) 은 0 이다 — 먼저 걸러내지 않으면 "storeId 없음" 이 storeId 0 으로 둔갑한다.
  if (rawStoreId === undefined) {
    return undefined;
  }

  // Number() 는 [] · '' · false 를 전부 0 으로 만든다. 그대로 두면 "id 없음" 이 storeId 0 으로
  // 둔갑해 엉뚱한 병원을 가리킬 수 있다. 숫자이거나 숫자로만 이루어진 문자열만 받는다.
  const isNumeric = typeof rawStoreId === 'number' || (typeof rawStoreId === 'string' && /^-?\d+$/.test(rawStoreId.trim()));

  if (!isNumeric) {
    return undefined;
  }

  const storeId = Number(rawStoreId);

  return Number.isFinite(storeId) && storeId > -1 ? storeId : undefined;
};

export const getAppStoresByUser = async (): Promise<AppStoreDto[] | undefined> => {
  try {
    const res: BaseApiResponse<AppStoreApiResponse[]> = await fetchRequest({
      endpoint: `/api/v1/app-user-store/list`,
      method: 'GET'
    });
    const { isSuccess, data } = checkHttpRequestResult(res);
    if (isSuccess) {
      return data?.map((AppStore) => {
        return {
          ...AppStore,
          channels: AppStore.storeChannelList
        };
      });
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching app stores by user:', error);
    return [];
  }
};

export const findAppStores = async (searchKeyword: string): Promise<AppStoreDto[] | void> => {
  try {
    const res: BaseApiResponse<AppStoreApiResponse[]> = await fetchRequest({
      endpoint: `/api/v1/stores/check?search=${searchKeyword}`,
      method: 'GET'
    });
    const { isSuccess, data } = checkHttpRequestResult(res);
    if (isSuccess) {
      return data?.map((AppStore) => {
        return {
          ...AppStore,
          channels: AppStore.storeChannelList
        };
      });
    } else {
      return [];
    }
  } catch (error) {
    // 조용히 삼키면 "검색 결과 없음" 과 "검색 실패" 가 화면에서 똑같이 보인다.
    console.error('findAppStores failed:', error);
  }
};

export const getAppStoreById = async (appStoreId: number): Promise<AppStoreDto | undefined> => {
  // 호출부가 8개 파일에 흩어져 있어 각자 막으면 반드시 한 곳이 샌다.
  // 요청을 만드는 이 지점 한 곳에서만 막는다(단일 초크포인트).
  if (!isAppStoreId(appStoreId)) {
    // -1 은 "선택된 병원 없음" 이라는 정상값이다. 조회할 게 없을 뿐이므로 오류가 아니다.
    if (appStoreId !== NO_APP_STORE_ID) {
      // ⚠️ 여기서 요청을 막으면 4xx 가 사라지고, 이 레포의 fetch 모니터가 자동으로 올려주던
      //    알림도 같이 없어진다(그게 지금까지 이 버그를 보여주던 유일한 신호였다).
      //    막되 보이게 하려고 명시적으로 올린다. console.warn 은 브라우저 콘솔에만 남으므로 보조다.
      // 메시지는 고정한다(값을 넣으면 fingerprint 가 값마다 갈린다).
      // 막힌 값은 '나갔을 뻔한 경로' 로 request.path 에 싣는다 — fingerprint 에는 안 들어간다.
      reportHandledError('getAppStoreById received a non-store id', { method: 'GET', path: `${STORE}/${String(appStoreId)}` });
      console.warn('getAppStoreById blocked an invalid store id:', appStoreId);
    }
    return undefined;
  }

  try {
    const res: BaseApiResponse<AppStoreApiResponse> = await fetchRequest({
      endpoint: `${STORE}/${appStoreId}`,
      method: 'GET'
    });
    const { isSuccess, result } = checkHttpRequestResult(res);
    if (isSuccess) {
      return {
        ...result,
        channels: result.storeChannelList
      };
    }
  } catch (error) {
    console.log(error);
  }
};

export const getAppStoreByStoreName = async (queryParams: { storeName: string; storeDetail: string }): Promise<AppStoreDto | undefined> => {
  try {
    const res: BaseApiResponse<AppStoreApiResponse> = await fetchRequest({
      endpoint: `/api/v1/store/search`,
      method: 'GET',
      body: queryParams
    });
    const { isSuccess, data, status, code, message } = checkHttpRequestResult(res);
    if (isSuccess) {
      // 미등록이면 성공(200)이지만 본문이 비어 온다. 그대로 펼치면 TypeError 가 나고
      // 아래 catch 가 그걸 삼켜 결과적으로만 맞는 undefined 가 됐다. 명시적으로 구분한다.
      if (!data) {
        return undefined;
      }

      return {
        ...data,
        channels: data.storeChannelList
      };
    }

    // 이 조회의 실패는 "그런 병원 없음" 과 반환값이 같다(undefined). 그대로 두면 조회 장애가
    // 신규 등록 시도로 이어지고 중복 등록 실패로 나타난다. fetchRequest 는 오류를 예외가 아니라
    // 값으로 돌려주므로 아래 catch 로는 잡히지 않는다 — 관측은 여기서 해야 한다.
    console.error('getAppStoreByStoreName failed:', { status, code, message });
  } catch (error) {
    console.error('getAppStoreByStoreName threw:', error);
  }
};

export const createAppStore = async (params: { storeName: string; storeDetail: string }): Promise<CreateAppStoreResult> => {
  try {
    const res: BaseApiResponse<AppStoreApiResponse> = await fetchRequest({
      endpoint: `/api/v1/store/register`,
      method: 'POST',
      body: params
    });
    const { isSuccess, data, status, code, message } = checkHttpRequestResult(res);

    if (isSuccess) {
      return {
        isSuccess: true,
        appStore: {
          ...data,
          channels: data.storeChannelList
        }
      };
    }

    return {
      isSuccess: false,
      status,
      code: readServerErrorCode(res) ?? code,
      message,
      duplicatedStoreId: readDuplicatedStoreId(res)
    };
  } catch (error) {
    // 여기서 삼키면 화면은 실패를 영영 모른다. 최소한 관측 가능하게 남기고 실패로 돌려준다.
    console.error('createAppStore failed:', error);

    return { isSuccess: false, message: '병원 등록 요청에 실패했습니다.' };
  }
};

export const matchUserAppStore = async ({ appStoreId, isNewAppStore = false }: { appStoreId: number; isNewAppStore?: boolean }): Promise<boolean | void> => {
  const params = {
    storeId: appStoreId,
    matchType: isNewAppStore ? 'REGISTRATION' : 'CHANGE'
  };

  try {
    const res: BaseApiResponse<{}> = await fetchRequest({
      endpoint: `/api/v1/app-user-store/match`,
      method: 'PATCH',
      body: params
    });
    const { isSuccess } = checkHttpRequestResult(res);

    return isSuccess;
  } catch (error) {
    console.error('matchUserAppStore failed:', error);
  }
};

export const getBuisinessNameByChannel = async (
  channelName: string,
  params: {
    query: string;
    page: number;
  }
): Promise<BuisinessNameSearchRes<BuisinessSearchResult> | void> => {
  try {
    const res: BaseApiResponse<BuisinessNameSearchRes<BuisinessSearchResult>> = await fetchRequest({
      endpoint: `/api/v1/store-channel/search/${channelName.toLowerCase()}`,
      method: 'GET',
      body: params
    });

    const result = checkHttpRequestResult(res);

    if (result.isSuccess) {
      return result.data;
    } else {
    }
  } catch (error) {}
};

export const getPosts = async (
  params: PostsDto,
  teamId?: string
): Promise<{
  data: Post[];
  sliceValue?: ListPageModel;
  totalCount: number | null;
} | void> => {
  try {
    const res: BaseApiResponse<{
      data: Post[];
      sliceValue?: ListPageModel;
      totalCount: number | null;
    }> = await fetchRequest({
      endpoint: REVIEWS,
      method: 'GET',
      body: {
        ...params,
        pageNo: params.pageNo ?? 1,
        pageSize: params.pageSize ?? POST_PAGGE_SIZE,
        ...(teamId ? { teamId } : {})
      }
    });

    return res.result;
  } catch (error) {
    console.error('getPosts error:', error);
  }
};

export const getPostsforStatistics = async (
  params: PostsDto,
  teamId?: string
): Promise<{
  data: Post[];
  sliceValue?: ListPageModel;
  totalCount: number;
} | void> => {
  try {
    const res: BaseApiResponse<{
      data: Post[];
      sliceValue?: ListPageModel;
      totalCount: number;
    }> = await fetchRequest({
      endpoint: `/api/v1/stores/${params.storeId}/posts/statistics`,
      method: 'GET',
      body: {
        ...params,
        ...(teamId ? { teamId } : {})
      }
    });

    return res.data;
  } catch (error) {
    console.error('getPosts error:', error);
  }
};

export const getPostsCountsByGroup = async (queryString: { storeId: number; channelIdList: ChannelId[] }): Promise<PostsCountsByGroup | void> => {
  try {
    const res: BaseApiResponse<PostsCountsByGroup> = await fetchRequest({
      endpoint: `/api/v1/posts/statistics/filter-count`,
      method: 'GET',
      body: queryString
    });
    return res.data;
  } catch (error) {}
};

export const getPostWords = async (queryString: { storeId: number; channelIdList: ChannelId[] }): Promise<PostWordData | undefined> => {
  try {
    const res: BaseApiResponse<{ statistics: PostWordData }> = await fetchRequest({
      endpoint: `/api/v1/post-word/statistics`,
      method: 'GET',
      body: queryString
    });

    const result = checkHttpRequestResult(res);

    return result.data?.statistics;
  } catch (error) {}
};

export const getAutoReplyForPost = async (postId: string): Promise<string | void> => {
  try {
    const res: BaseApiResponse<{
      result: string;
      status: string;
      replyAi: string;
    }> = await fetchRequest({
      endpoint: `/api/v1/post-reply-ai-recommend?postId=${postId}`,
      method: 'POST'
    });

    return res.data?.replyAi ?? '';
  } catch (error) {}
};

export const checkUserChannelAccounts = async (
  organizationId: string,
  teamId: string
): Promise<{
  MODOODOC: boolean;
  KAKAO_MAP: boolean;
  NAVER_MAP: boolean;
} | void> => {
  try {
    const res: BaseApiResponse<{
      MODOODOC: boolean;
      KAKAO_MAP: boolean;
      NAVER_MAP: boolean;
    }> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}/channel-accounts`,
      method: 'GET'
    });

    return res.data;
  } catch (error) {}
};

export const requestSubmitReply = async (
  organizationId: string,
  teamId: string,
  postId: string,
  channelId: ChannelId,
  storeName: string,
  reply: string
): Promise<
  | {
      result: CrawlingResultType;
      status: CrawlingResultStatusType;
    }
  | undefined
> => {
  try {
    const res: BaseApiResponse<ReplySubmitResponse> = await fetchRequest({
      endpoint: `/api/v1/post-reply`,
      method: 'POST',
      body: {
        organizationId,
        teamId,
        postId,
        channelId,
        storeName,
        reply
      }
    });
    if (res && res.data) {
      const { result, status } = res.data;
      return {
        result: result as CrawlingResultType,
        status: status as CrawlingResultStatusType
      };
    }
  } catch (error) {
    return { status: 'DATA_ERROR', result: 'FAIL' };
  }
};

export const requestLoginToChannel = async (params: {
  organizationId: string;
  teamId: string;
  channelId: ChannelId;
  storeName: string;
  accountId: string;
  accountPassword: string;
  storeId: number;
}): Promise<ReplySubmitResponse | undefined> => {
  try {
    const res: BaseApiResponse<ReplySubmitResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${params.organizationId}/teams/${params.teamId}/channel-accounts/login`,
      method: 'POST',
      body: params
    });

    const result = checkHttpRequestResult(res);
    return result.isSuccess ? result.data : undefined;
  } catch (error) {}
};

export const requestLoginToChannelAgain = async (params: { organizationId: string; teamId: string; channelId: ChannelId; storeName: string; storeId: number }): Promise<ReplySubmitResponse | void> => {
  try {
    const res: BaseApiResponse<ReplySubmitResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${params.organizationId}/teams/${params.teamId}/channel-accounts/relogin`,
      method: 'POST',
      body: params
    });
    return res.data ?? undefined;
  } catch (error) {}
};

export const requestAuthPasscodeToChannel = async (
  params: ChannelAccountAuthorizationDto,
  organizationId: string,
  teamId: string
): Promise<ReplySubmitResponse | { result: CrawlingResultType; status: CrawlingResultStatusType }> => {
  const { accountId, accountPassword, ...others } = params;

  try {
    const res: BaseApiResponse<ReplySubmitResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}/channel-accounts/kakao/sms`,
      method: 'POST',
      body: {
        ...others,
        userId: accountId,
        userPassword: accountPassword
      }
    });

    return res.data
      ? {
          ...res.data,
          result: res.data.result as CrawlingResultType,
          status: res.data.status as CrawlingResultStatusType
        }
      : {
          result: 'FAIL',
          status: 'DATA_ERROR'
        };
  } catch (error) {
    return {
      result: 'FAIL',
      status: 'DATA_ERROR'
    };
  }
};

export const requestPostCare = async (
  postId: string,
  productName: 'REVIEW_DELETE' | 'REVIEW_PUNISH' | 'THREAD_DELETE' | 'THREAD_PUNISH',
  organizationId: string,
  teamId: string
): Promise<{
  appUserName: string;
  postId?: string;
  threadId?: string;
} | void> => {
  const replacedProductName = productName.replace('THREAD', 'REVIEW');
  const endpoint = `/api/v1/products/${replacedProductName}/purchases`;
  const body: { organizationId: string; teamId: string; postId?: string; threadId?: string } = {
    organizationId,
    teamId
  };
  if (productName === 'THREAD_DELETE' || productName == 'THREAD_PUNISH') {
    body.threadId = postId;
  } else {
    body.postId = postId;
  }

  try {
    const res: BaseApiResponse<{
      appUserName: string;
      postId: string;
    }> = await fetchRequest({
      endpoint,
      method: 'POST',
      body
    });

    return res.data;
  } catch (error) {}
};

export const requestAddNewChannelForAppStore = async ({ appStoreId, channelId }: { appStoreId: number; channelId: ChannelId }): Promise<BaseApiResult<undefined> | undefined> => {
  try {
    const res: BaseApiResponse<{
      storeId: string;
      postId: string;
    }> = await fetchRequest({
      endpoint: '/api/v1/store/store-channel',
      method: 'POST',
      body: { storeId: appStoreId, channelId }
    });

    return checkHttpRequestResult(res);
  } catch (error) {}
};

export const getReivewCountsByPeriod = async ({
  appStoreId,
  channelIdList,
  period = 'MONTH'
}: {
  appStoreId: number;
  channelIdList: ChannelId[];
  period?: string;
}): Promise<BaseApiResult<ReviewCountByPeriod[]> | undefined> => {
  try {
    const res: BaseApiResponse<ReviewCountByPeriod[]> = await fetchRequest({
      endpoint: `/api/v1/stores/${appStoreId}/posts/statistics/period-count`,
      method: 'GET',
      body: { channelIdList, period }
    });

    return checkHttpRequestResult(res);
  } catch (error) {}
};

export const getOrganizationAndTeaminfo = async (appStoreId: number) => {
  try {
    const res: BaseApiResponse<{
      organizationId: string;
      teamId: string;
    }> = await fetchRequest({
      endpoint: `/api/v1/stores/${appStoreId}/organization-team`,
      method: 'GET'
    });

    return checkHttpRequestResult(res);
  } catch (error) {}
};
